import { useEffect } from 'react';
import './BoomburstAnimation.css';

/**
 * PROFESSIONAL BOOMBURST ANIMATION
 * Normal-type special move with massive sound wave explosion
 * 
 * Key Features:
 * - Normal-type special attack (140 base power, 100% accuracy)
 * - Hits all adjacent Pokémon
 * - Concentric sound wave rings with devastating shockwave
 * - Professional gaming quality with intense audio-visual impact
 */
export default function BoomburstAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Sound Energy Buildup (0-500ms)
    playerEl.classList.add('boomburst-user-charge');

    const chargeAura = document.createElement('div');
    chargeAura.className = 'boomburst-charge-aura';
    chargeAura.style.left = `${playerX - 80}px`;
    chargeAura.style.top = `${playerY - 80}px`;
    container.appendChild(chargeAura);
    elements.push(chargeAura);

    // Sound note particles gathering
    for (let i = 0; i < 14; i++) {
      const note = document.createElement('div');
      note.className = 'boomburst-note-particle';
      const angle = (i / 14) * 360;
      const radius = 70 + (i % 3) * 12;
      const noteX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const noteY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      note.style.left = `${noteX - 6}px`;
      note.style.top = `${noteY - 6}px`;
      note.style.animationDelay = `${i * 30}ms`;
      note.style.setProperty('--target-x', `${playerX - noteX}px`);
      note.style.setProperty('--target-y', `${playerY - noteY}px`);
      container.appendChild(note);
      elements.push(note);
    }

    // PHASE 2: Massive Sound Wave Blast (500-1200ms)
    const phase2Timer = setTimeout(() => {
      // Expanding concentric sound rings from player outward
      for (let i = 0; i < 6; i++) {
        const ringTimer = setTimeout(() => {
          const ring = document.createElement('div');
          ring.className = 'boomburst-sound-ring';
          ring.style.left = `${playerX - 100}px`;
          ring.style.top = `${playerY - 100}px`;
          ring.style.setProperty('--ring-scale', `${3 + i * 0.8}`);
          container.appendChild(ring);
          elements.push(ring);
        }, i * 80);
        elements.push({ timer: ringTimer, type: 'timer' });
      }

      // Sound distortion wave traveling to opponent
      const blastWave = document.createElement('div');
      blastWave.className = 'boomburst-blast-wave';
      blastWave.style.left = `${playerX}px`;
      blastWave.style.top = `${playerY}px`;
      blastWave.style.setProperty('--dx', `${dx}px`);
      blastWave.style.setProperty('--dy', `${dy}px`);
      container.appendChild(blastWave);
      elements.push(blastWave);

      // Shockwave distortion particles along path
      for (let i = 0; i < 16; i++) {
        const distortTimer = setTimeout(() => {
          const distort = document.createElement('div');
          distort.className = 'boomburst-distortion-particle';
          const progress = i / 16;
          const px = playerX + dx * progress + (Math.random() - 0.5) * 40;
          const py = playerY + dy * progress + (Math.random() - 0.5) * 40;
          distort.style.left = `${px - 8}px`;
          distort.style.top = `${py - 8}px`;
          container.appendChild(distort);
          elements.push(distort);
        }, i * 35);
        elements.push({ timer: distortTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Devastating Impact (1200-1800ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('boomburst-opponent-hit');

      // Massive explosion at opponent
      const explosion = document.createElement('div');
      explosion.className = 'boomburst-explosion';
      explosion.style.left = `${opponentX - 130}px`;
      explosion.style.top = `${opponentY - 130}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Impact shockwave rings at opponent
      for (let i = 0; i < 5; i++) {
        const impactRingTimer = setTimeout(() => {
          const impactRing = document.createElement('div');
          impactRing.className = 'boomburst-impact-ring';
          impactRing.style.left = `${opponentX - 80}px`;
          impactRing.style.top = `${opponentY - 80}px`;
          container.appendChild(impactRing);
          elements.push(impactRing);
        }, i * 100);
        elements.push({ timer: impactRingTimer, type: 'timer' });
      }

      // Sound burst debris particles
      for (let i = 0; i < 20; i++) {
        const debris = document.createElement('div');
        debris.className = 'boomburst-debris-particle';
        const debrisAngle = (i / 20) * 360;
        const debrisRadius = 70 + Math.random() * 50;
        debris.style.left = `${opponentX - 4}px`;
        debris.style.top = `${opponentY - 4}px`;
        debris.style.setProperty('--scatter-x', `${Math.cos(debrisAngle * Math.PI / 180) * debrisRadius}px`);
        debris.style.setProperty('--scatter-y', `${Math.sin(debrisAngle * Math.PI / 180) * debrisRadius}px`);
        debris.style.animationDelay = `${i * 15}ms`;
        container.appendChild(debris);
        elements.push(debris);
      }
    }, 1200);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Aftermath (1800-2200ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('boomburst-user-charge');

      // Residual distortion waves
      for (let i = 0; i < 8; i++) {
        const residual = document.createElement('div');
        residual.className = 'boomburst-residual-wave';
        residual.style.left = `${opponentX + (Math.random() - 0.5) * 120 - 10}px`;
        residual.style.top = `${opponentY + (Math.random() - 0.5) * 80 - 10}px`;
        residual.style.animationDelay = `${i * 40}ms`;
        container.appendChild(residual);
        elements.push(residual);
      }
    }, 1800);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2200ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('boomburst-user-charge');
      opponentEl.classList.remove('boomburst-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 2200);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('boomburst-user-charge');
      opponentEl.classList.remove('boomburst-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

BoomburstAnimation.moveType = 'normal';
